/** @author Rajkumar Rathod */
import { PROTOCOL_VERSION, RESERVED_QUERY_PARAMS, SDK_VERSION } from './constants';
import { Overlay } from './modal';
import { ALLOWED_EMBED_EVENTS, envelope, isEmbedMessage } from './protocol';
import { joinUrl, log, randomId } from './utils';
import { FLOW_MODAL_DEFAULTS, resolveModal, type ResolvedConfig } from './config';
import type { EmbedMessage, HostMessage } from './protocol';
import type { Emitter } from './emitter';
import type { CloseReason, OpenOptions, WidgetEventMap, WidgetEventName, WidgetFlow } from './types';

/**
 * One open widget. Owns the overlay, the iframe handshake and message routing, and
 * is discarded on close — nothing carries over to the next open.
 */
export class Session {
  readonly id = randomId();
  readonly flow: WidgetFlow;

  private readonly cfg: ResolvedConfig;
  private readonly emitter: Emitter;
  private readonly overlay: Overlay;
  private readonly opts: OpenOptions;
  private readonly onDisposed: () => void;

  private ready = false;
  private closed = false;
  private startedAt = 0;
  private readyTimer: number | undefined;

  constructor(
    cfg: ResolvedConfig,
    flow: WidgetFlow,
    opts: OpenOptions,
    emitter: Emitter,
    onDisposed: () => void
  ) {
    this.cfg = cfg;
    this.flow = flow;
    this.opts = opts;
    this.emitter = emitter;
    this.onDisposed = onDisposed;

    this.overlay = new Overlay(
      resolveModal(FLOW_MODAL_DEFAULTS[flow], cfg.modal, opts.modal),
      { theme: cfg.theme, sandbox: cfg.sandbox, allow: cfg.allow },
      { onRequestClose: (reason) => this.close(reason) }
    );
  }

  /** Build the embed URL, mount the overlay and start the handshake clock. */
  start(): void {
    const url = this.buildUrl();
    log.debug('opening', this.flow, url.toString(), 'session', this.id);

    window.addEventListener('message', this.handleMessage);
    this.startedAt = Date.now();
    this.overlay.mount(url.toString());
    this.emitter.emit('widget.opened', { flow: this.flow });

    // The only reliable failure detector for a cross-origin frame. A frame blocked by
    // `X-Frame-Options`/`frame-ancestors` still fires `load`, and we cannot read into
    // it, so a missing handshake is what tells us something went wrong.
    this.readyTimer = window.setTimeout(() => {
      if (this.ready || this.closed) return;
      // Seen by a patient on the client's website, so it says what they can do and
      // nothing about clientIds or allowlists — which are not even a cause unless a
      // deployment has chosen to gate /embed/*. The diagnosis goes to the console:
      // a timeout has several causes, and naming one sends whoever debugs it the
      // wrong way for as long as they believe it.
      this.overlay.showError('This form could not be loaded. Please try again in a moment.');
      this.fail(
        'embed_load_timeout',
        `No handshake from ${this.cfg.origin} within ${this.cfg.readyTimeoutMs}ms.`,
        `  embed url  ${url}\n` +
          `  host page  ${window.location.origin}\n` +
          `  clientId   ${this.cfg.clientId}\n` +
          `Causes, commonest first:\n` +
          `  1. ${this.cfg.host} does not really serve ${url.pathname}. A catch-all route\n` +
          `     answers 200 with an app shell, which looks deployed but never handshakes.\n` +
          `  2. That page does not set window.__EHR_PARENT_ORIGIN__, so the embed refuses\n` +
          `     to talk to this page.\n` +
          `  3. That page does not load the embed bundle for this flow (embed-${this.flow}.js).\n` +
          `  4. X-Frame-Options or frame-ancestors blocked the frame. A blocked frame still\n` +
          `     fires load, so this timeout is the only signal we get.\n` +
          `  5. The embed speaks a protocol version other than ${PROTOCOL_VERSION}.\n` +
          `  6. Only if you gate /embed/*: this origin is not registered for that clientId.`
      );
    }, this.cfg.readyTimeoutMs);
  }

  private buildUrl(): URL {
    const path = this.cfg.flows[this.flow] ?? `/embed/${this.flow}`;
    const url = joinUrl(this.cfg.host, path);

    // Caller params first, so the SDK's own keys below always overwrite them. Reserved
    // keys are dropped outright as well — belt and braces, because `origin` is the value
    // the EHR checks against the tenant allowlist and `sid` binds the handshake.
    for (const [k, v] of Object.entries(this.opts.params ?? {})) {
      if (RESERVED_QUERY_PARAMS.has(k)) {
        log.warn(`ignoring params.${k}: reserved by the SDK and not settable by the host`);
        continue;
      }
      url.searchParams.set(k, String(v));
    }

    // Query string carries only routing/identity — never patient data. It ends up in
    // referrer headers, server logs and browser history.
    url.searchParams.set('clientId', this.cfg.clientId);
    url.searchParams.set('flow', String(this.flow));
    url.searchParams.set('origin', window.location.origin);
    url.searchParams.set('sid', this.id);
    url.searchParams.set('sdk', SDK_VERSION);
    url.searchParams.set('pv', String(PROTOCOL_VERSION));
    url.searchParams.set('theme', this.cfg.theme);
    url.searchParams.set('locale', this.cfg.locale);
    url.searchParams.set('embed', '1');

    return url;
  }

  private readonly handleMessage = (event: MessageEvent): void => {
    // Origin check alone is not sufficient — any frame or tab on the EHR origin could
    // post to us. Pinning `source` to our own contentWindow is what makes this safe.
    if (event.origin !== this.cfg.origin) return;
    if (event.source !== this.overlay.iframe.contentWindow) return;
    if (!isEmbedMessage(event.data)) return;

    const msg = event.data as EmbedMessage;
    if (msg.sessionId !== this.id) {
      log.debug('dropping message for stale session', msg.sessionId);
      return;
    }

    log.debug('<- embed', msg.type, msg);

    switch (msg.type) {
      case 'embed:ready':
        this.onReady();
        break;
      case 'embed:resize':
        this.overlay.setContentHeight(msg.height);
        break;
      case 'embed:close':
        this.close('embed');
        break;
      case 'embed:token-request':
        void this.sendToken();
        break;
      case 'embed:event':
        this.forwardEvent(msg.name, msg.payload);
        break;
      case 'embed:error':
        this.overlay.showError(msg.message);
        this.fail(msg.code || 'embed_error', msg.message || 'The embedded app reported an error.');
        break;
    }
  };

  private onReady(): void {
    if (this.ready) return;
    this.ready = true;
    window.clearTimeout(this.readyTimer);
    this.overlay.markLoaded();

    void this.resolveToken().then((token) => {
      this.post({
        ...envelope(this.id),
        type: 'host:init',
        clientId: this.cfg.clientId,
        tenantId: this.cfg.tenantId,
        tenantName: this.cfg.tenantName,
        flow: this.flow,
        sdkVersion: SDK_VERSION,
        parentOrigin: window.location.origin,
        apiBaseUrl: this.cfg.apiBaseUrl,
        theme: this.cfg.theme,
        locale: this.cfg.locale,
        // Prefill travels here rather than in the URL: postMessage bodies are not
        // logged by servers, do not appear in history, and are not sent as referrer.
        prefill: { ...this.cfg.prefill, ...this.opts.prefill },
        token,
        hostRendersCloseButton: resolveModal(
          FLOW_MODAL_DEFAULTS[this.flow],
          this.cfg.modal,
          this.opts.modal
        ).showCloseButton,
      });
    });

    this.emitter.emit('widget.ready', { flow: this.flow, loadMs: Date.now() - this.startedAt });
  }

  private async resolveToken(): Promise<string | null> {
    const getter = this.cfg.getAuthToken;
    if (!getter) return null;
    try {
      return (await getter()) ?? null;
    } catch (err) {
      log.error('getAuthToken() threw; continuing unauthenticated:', err);
      return null;
    }
  }

  private async sendToken(): Promise<void> {
    const token = await this.resolveToken();
    if (this.closed) return;
    this.post({ ...envelope(this.id), type: 'host:token', token });
  }

  /** Re-emit an embed event locally, after checking it is one we publish. */
  private forwardEvent(name: WidgetEventName, payload: unknown): void {
    if (!ALLOWED_EMBED_EVENTS.has(name)) {
      log.warn(`ignoring unrecognised event "${name}" from embed`);
      return;
    }
    const body = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<
      string,
      unknown
    >;

    // `referenceId` is the host's own correlation id, so it is stamped *after* the
    // payload and the embed's copy is discarded. Spreading it first would let an event
    // claim it belongs to a record the host never tied this session to.
    const out: Record<string, unknown> = { ...body };
    const ref = this.opts.prefill?.referenceId ?? this.cfg.prefill.referenceId;
    if (ref === undefined) delete out['referenceId'];
    else out['referenceId'] = ref;

    this.emitter.emit(name, out as unknown as WidgetEventMap[WidgetEventName]);
  }

  /**
   * `detail` is logged but deliberately not emitted. `widget.error` is a public
   * payload a host may surface to users or ship to its own telemetry, so it stays a
   * short stable sentence; the debugging prose belongs in the console.
   */
  private fail(code: string, message: string, detail?: string): void {
    log.error(`${code}: ${message}${detail ? '\n' + detail : ''}`);
    this.emitter.emit('widget.error', { code, message, flow: this.flow });
  }

  private post(msg: HostMessage): void {
    const target = this.overlay.iframe.contentWindow;
    if (!target) return;
    log.debug('-> embed', msg.type);
    // Always an explicit target origin. '*' here would broadcast tokens and patient
    // prefill to whatever happens to occupy the frame.
    target.postMessage(msg, this.cfg.origin);
  }

  close(reason: CloseReason): void {
    if (this.closed) return;
    this.closed = true;
    window.clearTimeout(this.readyTimer);

    if (this.ready) this.post({ ...envelope(this.id), type: 'host:close', reason });

    window.removeEventListener('message', this.handleMessage);
    this.overlay.destroy();
    this.emitter.emit('widget.closed', { flow: this.flow, reason });
    this.onDisposed();
  }
}
